/** @jsxImportSource @emotion/react */

import { FC } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { Button, CircularProgress, Typography } from '@mui/material'
import { BaseModal } from 'components/Modals/BaseModal/BaseModal'
import { GET_LIST_QUESTION, GET_LIST_DELETED_QUESTION, SEARCH_TEMPLATE_QUESTION } from 'queries/question'
import { findAllQuestion, findAllQuestionVariables } from '__generatedTypes__/findAllQuestion'
import { findAllDeletedQuestion, findAllDeletedQuestionVariables } from '__generatedTypes__/findAllDeletedQuestion'
import { searchTemplateQuestion, searchTemplateQuestionVariables } from '__generatedTypes__/searchTemplateQuestion'
import { findAllQuestionCategory_findAllQuestionCategory_items as QuestionCategory } from '__generatedTypes__/findAllQuestionCategory'
import {
  QUESTIONS_TAB,
  QUESTION_TEMPLATES_TAB,
  QUERY_PARAM as TAB_QUERY_PARAM,
} from 'pagesNew/Questions/QuestionTabs/QuestionTabs'
import { QUESTION_CATEGORY_PARAM, SHOW_DELETED_PARAM } from 'pagesNew/Questions/List/consts'
import { routes, useLink } from 'routes/cmsRoutes'
import * as classes from './styles'

interface IProps {
  open: boolean
  questionCategory: QuestionCategory
  onClose: () => void
}

export const CannotDeleteCategoryModal: FC<IProps> = ({ open, questionCategory, onClose }) => {
  const { platform } = useParams<{ platform: string }>()

  const { getLink } = useLink()

  const categoryFilter = { where: { questionCategory: { id: questionCategory.id }, isTemplate: false }, take: 1 }

  const { data: publishedData, loading: loadingPublished } = useQuery<findAllQuestion, findAllQuestionVariables>(
    GET_LIST_QUESTION,
    { variables: { params: categoryFilter }, fetchPolicy: 'network-only', skip: !open }
  )

  const { data: deletedData, loading: loadingDeleted } = useQuery<
    findAllDeletedQuestion,
    findAllDeletedQuestionVariables
  >(GET_LIST_DELETED_QUESTION, {
    variables: { params: categoryFilter },
    fetchPolicy: 'network-only',
    skip: !open,
  })

  const { data: templatesData, loading: loadingTemplates } = useQuery<
    searchTemplateQuestion,
    searchTemplateQuestionVariables
  >(SEARCH_TEMPLATE_QUESTION, {
    variables: { questionCategoryId: questionCategory.id, take: 1 },
    fetchPolicy: 'network-only',
    skip: !open,
  })

  const rows = [
    {
      label: 'Questions',
      count: publishedData?.findAllQuestion.count ?? 0,
      loading: loadingPublished,
      href: getLink(
        routes.QUESTION_LIST,
        { platform: platform as string },
        new URLSearchParams({
          [TAB_QUERY_PARAM]: QUESTIONS_TAB.id,
          [QUESTION_CATEGORY_PARAM]: questionCategory.id,
        })
      ),
    },
    {
      label: 'Deleted Questions',
      count: deletedData?.findAllDeletedQuestion.count ?? 0,
      loading: loadingDeleted,
      href: getLink(
        routes.QUESTION_LIST,
        { platform: platform as string },
        new URLSearchParams({
          [TAB_QUERY_PARAM]: QUESTIONS_TAB.id,
          [QUESTION_CATEGORY_PARAM]: questionCategory.id,
          [SHOW_DELETED_PARAM]: 'true',
        })
      ),
    },
    {
      label: 'Templates',
      count: templatesData?.searchTemplateQuestion.count ?? 0,
      loading: loadingTemplates,
      href: getLink(
        routes.QUESTION_LIST,
        { platform: platform as string },
        new URLSearchParams({
          [TAB_QUERY_PARAM]: QUESTION_TEMPLATES_TAB.id,
          [QUESTION_CATEGORY_PARAM]: questionCategory.id,
        })
      ),
    },
  ]

  return (
    <BaseModal open={open} onClose={onClose}>
      <div css={classes.container}>
        <Typography variant="title2" css={classes.title}>
          Cannot Delete Question Category
        </Typography>
        <Typography variant="body3" css={classes.subtitle}>
          &quot;{questionCategory.title}&quot; is currently in use and cannot be deleted.
        </Typography>
        <div css={classes.rows}>
          {rows.map(({ label, count, loading, href }) => (
            <div key={label} css={classes.row}>
              <Typography variant="body2">{label}</Typography>
              <div css={classes.countAndLink}>
                {loading ? (
                  <CircularProgress size={14} />
                ) : (
                  <Typography variant="body2" css={classes.count}>
                    {count}
                  </Typography>
                )}
                <a href={href} target="_blank" rel="noreferrer" css={classes.link}>
                  View
                </a>
              </div>
            </div>
          ))}
        </div>
        <Button fullWidth size="large" variant="outlined" onClick={onClose}>
          Close
        </Button>
      </div>
    </BaseModal>
  )
}
